import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import API_URL from '../../apiConfig';
import './MeditationsSection.css';

const MeditationsSection = () => {
    const trackRef = useRef(null);
    const outerRef = useRef(null);
    const [meditations, setMeditations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentOffset, setCurrentOffset] = useState(0);
    const [maxOffset, setMaxOffset] = useState(0);
    const [selectedMedit, setSelectedMedit] = useState(null);

    const updateMax = useCallback(() => {
        if (trackRef.current && outerRef.current) {
            const max = Math.max(0, trackRef.current.scrollWidth - outerRef.current.offsetWidth);
            setMaxOffset(max);
        }
    }, []);

    useEffect(() => {
        if (!loading) {
            const t = setTimeout(updateMax, 200);
            return () => clearTimeout(t);
        }
    }, [loading, meditations, updateMax]);

    useEffect(() => {
        window.addEventListener('resize', updateMax);
        return () => window.removeEventListener('resize', updateMax);
    }, [updateMax]);

    const getStep = () => {
        const card = trackRef.current?.querySelector('.meditation-site-card');
        return card ? card.offsetWidth + 20 : 370;
    };

    const scrollPrev = () => {
        setCurrentOffset(prev => Math.max(0, prev - getStep()));
    };

    const scrollNext = () => {
        setCurrentOffset(prev => Math.min(maxOffset, prev + getStep()));
    };

    const canPrev = currentOffset > 0;
    const canNext = currentOffset < maxOffset - 5; // offset marge

    const getImageSrc = (linkImg) => {
        if (!linkImg) return 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=600';
        let first = linkImg;
        try {
            if (Array.isArray(linkImg)) {
                first = linkImg[0];
            } else if (typeof linkImg === 'string') {
                if (linkImg.startsWith('[')) {
                    first = JSON.parse(linkImg)[0];
                } else if (linkImg.includes(',')) {
                    first = linkImg.split(',')[0];
                }
            }
            if (!first) return 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=600';
            return first.startsWith('http') ? first : `${API_URL}${first.startsWith('/') ? '' : '/'}${first}`;
        } catch (e) {
            return (typeof linkImg === 'string' && linkImg.startsWith('http')) ? linkImg : `${API_URL}${linkImg && linkImg.startsWith('/') ? '' : '/'}${linkImg}`;
        }
    };

    const fetchTopMeditations = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/api/meditations/allmeditations`, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
                credentials: "include"
            });
            const data = await res.json();
            if (data.success) {
                const published = data.meditations
                    .filter(m => Number(m.published || m.publie) === 1)
                    .slice(0, 10);
                setMeditations(published);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchTopMeditations(); }, []);

    return (
        <section className="meditations-site-section py-5 bg-light" id="nos-meditations">
            <div className="container py-lg-5">
                <div className="d-flex justify-content-between align-items-center mb-5 px-3 px-md-0">
                    <div>
                        <span className="text-primary fw-bold text-uppercase small tracking-widest d-block mb-1">Inspiration & Parole</span>
                        <h2 className="section-m-title outfit display-5 fw-bold text-dark mb-0">Méditations Récentes</h2>
                    </div>
                    <Link to="/nos-meditations" className="btn btn-outline-primary rounded-pill px-4 fw-bold shadow-sm d-none d-md-flex align-items-center">
                        Voir tout <i className="bi bi-arrow-right-short fs-4 ms-1"></i>
                    </Link>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}></div>
                    </div>
                ) : meditations.length === 0 ? (
                    <p className="text-muted text-center py-5">Aucune méditation disponible pour le moment.</p>
                ) : (
                    <div className="meditations-carousel-wrapper position-relative">
                        <div className="meditations-carousel-outer" ref={outerRef} style={{ overflow: 'hidden' }}>
                            <div
                                className="meditations-carousel-track d-flex gap-4"
                                ref={trackRef}
                                style={{ transform: `translateX(-${currentOffset}px)`, transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                            >
                                {meditations.map(m => (
                                    <div 
                                        key={m.idMedit} 
                                        className="meditation-site-card flex-shrink-0 cursor-pointer" 
                                        style={{ width: '380px' }}
                                        onClick={() => setSelectedMedit(m)}
                                    >
                                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden h-100 meditations-card-hover bg-white border-light border">
                                            <div className="position-relative" style={{ height: '280px', overflow: 'hidden' }}>
                                                <img
                                                    src={getImageSrc(m.linkImg)}
                                                    alt="medit"
                                                    className="w-100 h-100 object-cover transition-img"
                                                />
                                                <div className="position-absolute bottom-0 start-0 m-3 z-2">
                                                    <span className="badge bg-primary rounded-pill px-3 py-2 shadow-lg border-white border text-uppercase" style={{ fontSize: '0.7rem' }}>{m.type}</span>
                                                </div>
                                                <div className="img-overlay"></div>
                                            </div>
                                            <div className="card-body p-4 d-flex flex-column h-100">
                                                <h5 className="card-title fw-bold text-dark mb-3 text-truncate-2" style={{ lineHeight: '1.4', fontSize: '1.25rem' }}>{m.textMedit}</h5>
                                                <div className="d-flex justify-content-between align-items-center mt-auto pt-3 border-top border-light">
                                                    <div className="text-muted small fw-medium">
                                                        <i className="bi bi-calendar3 me-1"></i>
                                                        {new Date(m.datePublication).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                    </div>
                                                    <div className="btn p-0 text-primary fw-bold small text-uppercase">
                                                        Lire <i className="bi bi-chevron-right ms-1" style={{ fontSize: '0.7rem' }}></i>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Flèches de navigation */}
                        <div className="carousel-nav-btns mt-5 d-flex justify-content-center gap-3">
                            <button
                                className={`btn btn-white shadow-sm rounded-circle d-flex align-items-center justify-content-center border ${!canPrev ? 'opacity-25' : 'hover-primary'}`}
                                style={{ width: '55px', height: '55px' }}
                                onClick={(e) => { e.stopPropagation(); scrollPrev(); }}
                                disabled={!canPrev}
                            >
                                <i className="bi bi-arrow-left fs-5"></i>
                            </button>
                            <button
                                className={`btn btn-white shadow-sm rounded-circle d-flex align-items-center justify-content-center border ${!canNext ? 'opacity-25' : 'hover-primary'}`}
                                style={{ width: '55px', height: '55px' }}
                                onClick={(e) => { e.stopPropagation(); scrollNext(); }}
                                disabled={!canNext}
                            >
                                <i className="bi bi-arrow-right fs-5"></i>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal de Lecture Méditation */}
            {selectedMedit && (
                <div className="medit-modal-overlay">
                    <style>{`
                        .site-header { display: none !important; }
                        .medit-modal-overlay {
                            position: fixed; top: 0; left: 0; width: 100%; height: 100%;
                            background: rgba(0,0,0,0.95); z-index: 9999;
                            display: flex; align-items: center; justify-content: center;
                            padding: 20px;
                        }
                        .medit-modal-content {
                            background: white; width: 100%; max-width: 1000px; max-height: 90vh;
                            border-radius: 25px; overflow-y: auto; position: relative;
                            box-shadow: 0 30px 60px rgba(0,0,0,0.5);
                            animation: modalScale 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                        }
                        @keyframes modalScale { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
                    `}</style>
                    <div className="medit-modal-content animate__animated animate__zoomIn animate__faster">
                        <button 
                             className="btn-close-custom bg-dark text-white rounded-circle border-0 shadow" 
                             onClick={() => setSelectedMedit(null)}
                             style={{ position: 'absolute', top: '20px', right: '20px', width: '40px', height: '40px', zIndex: 10 }}
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>

                        <div className="row g-0">
                            <div className="col-lg-6">
                                <div style={{ height: '100%', minHeight: '400px' }}>
                                    <img 
                                        src={getImageSrc(selectedMedit.linkImg)} 
                                        alt="medit-full" 
                                        className="w-100 h-100 object-cover" 
                                    />
                                </div>
                            </div>
                            <div className="col-lg-6 p-4 p-md-5 d-flex flex-column justify-content-center">
                                <div className="mb-4">
                                    <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 rounded-pill text-uppercase mb-3" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>
                                        {selectedMedit.type}
                                    </span>
                                    <div className="text-muted small mb-2"><i className="bi bi-calendar3 me-1"></i> {new Date(selectedMedit.datePublication).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                                    <h2 className="display-6 fw-bold outfit text-dark mb-0">La Méditation</h2>
                                </div>
                                <div className="meditation-full-text text-muted mb-5 lh-lg" style={{ whiteSpace: 'pre-wrap', fontSize: '1.2rem' }}>
                                    {selectedMedit.textMedit}
                                </div>
                                <div className="p-4 bg-primary bg-opacity-5 rounded-4 border-start border-4 border-primary mb-4">
                                    <p className="italic text-primary mb-1 fw-medium" style={{ fontSize: '1.05rem' }}>
                                        "Ta parole est une lampe à mes pieds, et une lumière sur mon sentier."
                                    </p>
                                    <span className="fw-bold text-dark small">— Psaumes 119:105</span>
                                </div>
                                <div className="d-grid mt-2">
                                    <button className="btn btn-primary btn-lg rounded-pill shadow-lg fw-bold py-3">
                                        <i className="bi bi-share-fill me-2"></i> PARTAGER CETTE PAROLE
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <style>{`
                .meditations-card-hover { transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1); }
                .meditations-card-hover:hover { transform: translateY(-10px); box-shadow: 0 20px 40px rgba(0,0,0,0.12) !important; }
                .meditations-card-hover:hover .transition-img { transform: scale(1.08); }
                .transition-img { transition: transform 0.8s ease; }
                .hover-primary:hover { background: var(--primary-color) !important; color: white !important; }
                .img-overlay { position: absolute; top:0; left:0; width:100%; height:100%; background: linear-gradient(to top, rgba(0,0,0,0.2), transparent); }
                .tracking-widest { letter-spacing: 0.2em; }
            `}</style>
        </section>
    );
};

export default MeditationsSection;
